import { useStorageState } from '@luminovo/frontend-utils';
import * as React from 'react';
import type { JSX } from 'react';

import { AvailabilityRenderOption, DisplayFormat } from '../TanStackTable/type';

const DEFAULT_DISPLAY_FORMAT: DisplayFormat = {
    leadTime: 'days',
};

/** Bump the version when the stored shape changes incompatibly, as with `TAN_STACK_TABLE_STATE_KEY`. */
const DISPLAY_FORMAT_STORAGE_KEY = 'luminovo.display-format.v1';

export interface DisplayFormatContextValue {
    /** Every entry is always present. */
    displayFormat: DisplayFormat;
    setDisplayFormat: (update: Partial<DisplayFormat>) => void;
}

const isAvailabilityRenderOption = (value: unknown): value is AvailabilityRenderOption =>
    value === 'days' || value === 'weeks';

const isPartialDisplayFormat = (value: unknown): value is Partial<DisplayFormat> => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Record<string, unknown>;
    return !('leadTime' in candidate) || isAvailabilityRenderOption(candidate.leadTime);
};

/** For when no provider is mounted, e.g. in Storybook or tests. */
const FALLBACK_VALUE: DisplayFormatContextValue = {
    displayFormat: DEFAULT_DISPLAY_FORMAT,
    setDisplayFormat: () => {},
};

const DisplayFormatContext = React.createContext<DisplayFormatContextValue | null>(null);

/** App-wide display preferences, kept in localStorage and shared so every consumer updates at once. */
export function DisplayFormatProvider({ children }: React.PropsWithChildren<{}>): JSX.Element {
    const [stored, setStored] = useStorageState<Partial<DisplayFormat>>(
        DISPLAY_FORMAT_STORAGE_KEY,
        {},
        {
            storage: localStorage,
            enabled: true,
            validation: isPartialDisplayFormat,
        },
    );

    // Depends only on the stable setter, so consumers with it in effect deps don't re-run on every change.
    const setDisplayFormat = React.useCallback(
        (update: Partial<DisplayFormat>) => setStored((prev) => ({ ...prev, ...update })),
        [setStored],
    );

    const displayFormat = React.useMemo<DisplayFormat>(() => ({ ...DEFAULT_DISPLAY_FORMAT, ...stored }), [stored]);

    const value = React.useMemo<DisplayFormatContextValue>(
        () => ({ displayFormat, setDisplayFormat }),
        [displayFormat, setDisplayFormat],
    );

    return <DisplayFormatContext.Provider value={value}>{children}</DisplayFormatContext.Provider>;
}

/** Falls back to read-only defaults without a `DisplayFormatProvider`. */
export function useDisplayFormat(): DisplayFormatContextValue {
    return React.useContext(DisplayFormatContext) ?? FALLBACK_VALUE;
}
